function getSessionStorage(): Storage | null {
  if (typeof window === "undefined") return null;

  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function getItem(key: string): string | null {
  try {
    return getSessionStorage()?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

function setItem(key: string, value: string): boolean {
  const storage = getSessionStorage();

  if (!storage) return false;

  try {
    storage.setItem(key, value);

    return true;
  } catch {
    // Storage is optional when browser privacy settings deny access.
    return false;
  }
}

function removeItem(key: string): void {
  try {
    getSessionStorage()?.removeItem(key);
  } catch {
    // Storage is optional when browser privacy settings deny access.
  }
}

function clear(): void {
  try {
    getSessionStorage()?.clear();
  } catch {
    // Storage is optional when browser privacy settings deny access.
  }
}

/**
 * Provides SSR-safe, failure-tolerant access to browser session storage.
 *
 * @remarks
 * `setItem` reports whether the value was actually persisted. Callers that use
 * a key as a once-only guard must fail closed when it returns `false`, because
 * a silently dropped write turns a retry guard into an unbounded loop.
 *
 * @example
 * const storedValue = safeSessionStorage.getItem("navigation_history");
 * safeSessionStorage.setItem("navigation_history", "[]");
 */
export const safeSessionStorage = {
  getItem,
  setItem,
  removeItem,
  clear,
};
